import { getConfig } from './config';
import { getIdToken } from './auth';

/**
 * A direct implementation of the AppSync realtime (WebSocket) protocol.
 *
 * Amplify's client would do this for us, but it drags in native modules whose
 * react-native-macos/windows support is uneven. The protocol is small and the
 * app has to run identically on three platforms, so it is implemented here
 * against the plain global WebSocket that every RN variant provides.
 *
 * Protocol: connect with a base64 auth header in the query string, send
 * `connection_init`, wait for `connection_ack`, then `start` one frame per
 * subscription. The server sends `ka` keep-alives; silence past the negotiated
 * timeout means the connection is dead and we reconnect.
 */

type ServerMessage =
  | { type: 'connection_ack'; payload?: { connectionTimeoutMs?: number } }
  | { type: 'ka' }
  | { type: 'start_ack'; id: string }
  | { type: 'data'; id: string; payload: { data: unknown } }
  | { type: 'error'; id?: string; payload?: unknown }
  | { type: 'complete'; id: string }
  | { type: 'connection_error'; payload?: unknown };

export interface SubscriptionHandlers<T> {
  next: (value: T) => void;
  error?: (err: Error) => void;
}

interface ActiveSub {
  id: string;
  query: string;
  variables: Record<string, unknown>;
  handlers: SubscriptionHandlers<any>;
  /** Field name to unwrap from the payload, e.g. "onMessage". */
  field: string;
  started: boolean;
}

export type ConnectionState = 'closed' | 'connecting' | 'ready';

const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;

/** btoa is not available in React Native; the input here is always ASCII JSON. */
function base64(input: string): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const a = input.charCodeAt(i);
    const b = input.charCodeAt(i + 1);
    const c = input.charCodeAt(i + 2);
    const bitmap = (a << 16) | ((isNaN(b) ? 0 : b) << 8) | (isNaN(c) ? 0 : c);
    out +=
      chars[(bitmap >> 18) & 63]! +
      chars[(bitmap >> 12) & 63]! +
      (isNaN(b) ? '=' : chars[(bitmap >> 6) & 63]!) +
      (isNaN(c) ? '=' : chars[bitmap & 63]!);
  }
  return out;
}

let subCounter = 0;
const nextSubId = () => `sub-${++subCounter}-${Math.random().toString(36).slice(2, 8)}`;

class RealtimeClient {
  private socket: WebSocket | null = null;
  private state: ConnectionState = 'closed';
  private subs = new Map<string, ActiveSub>();
  private kaTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionTimeoutMs = 300_000;
  private attempt = 0;
  private stateListeners = new Set<(s: ConnectionState) => void>();
  /** Set when the app deliberately signs off, to suppress auto-reconnect. */
  private stopped = false;

  onStateChange(fn: (s: ConnectionState) => void): () => void {
    this.stateListeners.add(fn);
    fn(this.state);
    return () => this.stateListeners.delete(fn);
  }

  private setState(s: ConnectionState) {
    if (this.state === s) return;
    this.state = s;
    for (const fn of this.stateListeners) fn(s);
  }

  subscribe<T>(
    query: string,
    variables: Record<string, unknown>,
    field: string,
    handlers: SubscriptionHandlers<T>
  ): () => void {
    const id = nextSubId();
    const sub: ActiveSub = { id, query, variables, handlers, field, started: false };
    this.subs.set(id, sub);
    this.stopped = false;

    if (this.state === 'ready') {
      this.start(sub);
    } else {
      void this.connect();
    }

    return () => {
      const s = this.subs.get(id);
      this.subs.delete(id);
      if (s?.started && this.socket?.readyState === WebSocket.OPEN) {
        this.send({ id, type: 'stop' });
      }
      // Keep the socket open briefly; opening a fresh one per IM window would
      // be wasteful when the user closes and reopens conversations.
      if (this.subs.size === 0) this.scheduleIdleClose();
    };
  }

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private scheduleIdleClose() {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      if (this.subs.size === 0) this.close();
    }, 10_000);
  }

  private async connect(): Promise<void> {
    if (this.state !== 'closed' || this.stopped) return;
    this.setState('connecting');

    let token: string;
    try {
      token = await getIdToken();
    } catch (err) {
      // No valid session — the UI will bounce the user to sign-on; do not spin.
      this.setState('closed');
      this.failAll(err as Error);
      return;
    }

    const cfg = getConfig();
    const host = new URL(cfg.graphqlUrl).host;
    const header = base64(JSON.stringify({ host, Authorization: token }));
    const url = `${cfg.realtimeUrl}?header=${encodeURIComponent(header)}&payload=e30=`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url, 'graphql-ws');
    } catch (err) {
      this.setState('closed');
      this.scheduleReconnect();
      return;
    }
    this.socket = ws;

    ws.onopen = () => this.send({ type: 'connection_init' });
    ws.onmessage = (e) => this.onMessage(e);
    ws.onerror = () => {
      /* onclose always follows; reconnect is handled there */
    };
    ws.onclose = () => {
      if (this.socket === ws) {
        this.socket = null;
        this.setState('closed');
        this.clearKaTimer();
        for (const s of this.subs.values()) s.started = false;
        this.scheduleReconnect();
      }
    };
  }

  // React Native types the event's `data` as optional-any, so it is narrowed
  // here rather than at the call site. AppSync only ever sends text frames.
  private onMessage(event: { data?: any }) {
    if (typeof event.data !== 'string') return;
    let msg: ServerMessage;
    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }

    switch (msg.type) {
      case 'connection_ack': {
        this.connectionTimeoutMs = msg.payload?.connectionTimeoutMs ?? 300_000;
        this.attempt = 0;
        this.setState('ready');
        this.resetKaTimer();
        for (const sub of this.subs.values()) this.start(sub);
        break;
      }
      case 'ka':
        this.resetKaTimer();
        break;
      case 'start_ack': {
        const sub = this.subs.get(msg.id);
        if (sub) sub.started = true;
        break;
      }
      case 'data': {
        const sub = this.subs.get(msg.id);
        if (!sub) return;
        const data = msg.payload?.data as Record<string, unknown> | undefined;
        const value = data?.[sub.field];
        // AppSync sends null for a filtered-out event; nothing to deliver.
        if (value != null) sub.handlers.next(value);
        break;
      }
      case 'error': {
        const err = new Error(describeError(msg.payload));
        if (msg.id) {
          this.subs.get(msg.id)?.handlers.error?.(err);
        } else {
          this.failAll(err);
        }
        break;
      }
      case 'connection_error':
        this.failAll(new Error(describeError(msg.payload)));
        break;
      case 'complete': {
        this.subs.get(msg.id)?.handlers.error?.(new Error('Subscription ended.'));
        this.subs.delete(msg.id);
        break;
      }
    }
  }

  private async start(sub: ActiveSub) {
    if (sub.started || this.socket?.readyState !== WebSocket.OPEN) return;
    let token: string;
    try {
      token = await getIdToken();
    } catch (err) {
      sub.handlers.error?.(err as Error);
      return;
    }
    const host = new URL(getConfig().graphqlUrl).host;
    this.send({
      id: sub.id,
      type: 'start',
      payload: {
        data: JSON.stringify({ query: sub.query, variables: sub.variables }),
        extensions: { authorization: { host, Authorization: token } },
      },
    });
  }

  private send(obj: unknown) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(obj));
    }
  }

  private resetKaTimer() {
    this.clearKaTimer();
    // Missing keep-alives is the only signal a silently-dropped connection
    // gives us — mobile networks routinely close sockets without a FIN.
    this.kaTimer = setTimeout(() => this.socket?.close(), this.connectionTimeoutMs);
  }

  private clearKaTimer() {
    if (this.kaTimer) {
      clearTimeout(this.kaTimer);
      this.kaTimer = null;
    }
  }

  private scheduleReconnect() {
    if (this.stopped || this.subs.size === 0 || this.reconnectTimer) return;
    const delay = Math.min(BASE_BACKOFF_MS * 2 ** this.attempt++, MAX_BACKOFF_MS);
    // Jitter so a service blip does not bring every client back in lockstep.
    const jittered = delay * (0.5 + Math.random() * 0.5);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, jittered);
  }

  private failAll(err: Error) {
    for (const sub of this.subs.values()) sub.handlers.error?.(err);
  }

  /** Tear everything down — used on sign off. */
  close() {
    this.stopped = true;
    this.subs.clear();
    this.clearKaTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
    this.setState('closed');
  }

  /** Force a reconnect, e.g. when the app returns to the foreground. */
  refresh() {
    if (this.subs.size === 0) return;
    this.attempt = 0;
    this.socket?.close();
  }
}

function describeError(payload: unknown): string {
  const errors = (payload as { errors?: Array<{ message?: string }> } | undefined)?.errors;
  return errors?.[0]?.message ?? 'Realtime connection error.';
}

export const realtime = new RealtimeClient();
